import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { FileText, Upload, Download, Trash2, Camera, Eye } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "@/App";
import { formatFileSize, toggleStudentSelection, handleBlobDownload } from "./utils";

export function DocumentsTab({ classId, documents, students, isTeacher, user, headers, onRefresh }) {
  const [docUploadDialog, setDocUploadDialog] = useState(false);
  const [docUploadFiles, setDocUploadFiles] = useState([]);
  const [docDescription, setDocDescription] = useState("");
  const [docShareMode, setDocShareMode] = useState("all");
  const [docSelectedStudents, setDocSelectedStudents] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const fileInputRef = useRef(null);
  const docCameraRef = useRef(null);

  const addFiles = (fileList) => {
    if (!fileList) return;
    const newFiles = Array.from(fileList);
    setDocUploadFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setDocUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setDocUploadFiles([]);
    setDocDescription("");
    setDocShareMode("all");
    setDocSelectedStudents([]);
    setUploadProgress("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (docCameraRef.current) docCameraRef.current.value = "";
  };

  const handleFileUpload = async () => {
    if (docUploadFiles.length === 0) {
      toast.error("Please select at least one file");
      return;
    }
    setUploading(true);
    let successCount = 0;
    const total = docUploadFiles.length;

    for (let i = 0; i < total; i++) {
      const file = docUploadFiles[i];
      setUploadProgress(`Uploading ${i + 1} of ${total}: ${file.name}`);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("class_id", classId);
      formData.append("description", docDescription);
      if (isTeacher && docShareMode === "specific" && docSelectedStudents.length > 0) {
        formData.append("shared_with", docSelectedStudents.join(","));
      }
      try {
        await axios.post(`${API}/documents`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        successCount++;
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? "s" : ""} uploaded successfully!`);
    }
    setDocUploadDialog(false);
    resetForm();
    onRefresh();
    setUploading(false);
  };

  const handleDownload = (docId, filename) => {
    handleBlobDownload(`${API}/documents/${docId}/download`, filename, headers);
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, { headers });
      toast.success("Document deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <CardTitle className="font-['Manrope']">Documents</CardTitle>
          <CardDescription>Class materials and resources</CardDescription>
        </div>
        <div className="shrink-0">
          <Dialog open={docUploadDialog} onOpenChange={(open) => {
            setDocUploadDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-primary text-primary-foreground" data-testid="upload-document-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Upload Documents</DialogTitle>
                <DialogDescription>Upload one or more files, or take a photo</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
                  <input type="file" multiple ref={fileInputRef} onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} className="hidden" data-testid="doc-file-input" />
                  <input type="file" accept="image/*" capture="environment" ref={docCameraRef} onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} className="hidden" data-testid="doc-camera-input" />
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">
                    {docUploadFiles.length > 0 ? `${docUploadFiles.length} file${docUploadFiles.length > 1 ? "s" : ""} selected` : "Choose files or take a photo"}
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="doc-choose-file-btn">
                      <FileText className="w-4 h-4 mr-1" />
                      {docUploadFiles.length > 0 ? "Add More" : "Choose Files"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => docCameraRef.current?.click()} data-testid="doc-camera-btn">
                      <Camera className="w-4 h-4 mr-1" />
                      Take Photo
                    </Button>
                  </div>
                </div>
                {docUploadFiles.length > 0 && (
                  <div className="max-h-40 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
                    {docUploadFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-2 p-1.5 rounded hover:bg-muted/50">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-primary shrink-0" />
                          <span className="text-sm text-foreground truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">{formatFileSize(file.size)}</span>
                        </div>
                        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => removeFile(index)} data-testid={`remove-file-${index}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input placeholder="Brief description..." value={docDescription} onChange={(e) => setDocDescription(e.target.value)} data-testid="doc-description-input" />
                </div>
                {isTeacher && students.length > 0 && (
                  <div className="space-y-2">
                    <Label>Share with</Label>
                    <div className="flex gap-3">
                      <button onClick={() => setDocShareMode("all")} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all ${docShareMode === "all" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid="doc-share-all-btn">
                        All Students
                      </button>
                      <button onClick={() => setDocShareMode("specific")} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all ${docShareMode === "specific" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid="doc-share-specific-btn">
                        Specific Students
                      </button>
                    </div>
                    {docShareMode === "specific" && (
                      <div className="max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
                        {students.map(s => (
                          <label key={s.user_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer text-sm">
                            <input type="checkbox" checked={docSelectedStudents.includes(s.user_id)} onChange={() => toggleStudentSelection(s.user_id, setDocSelectedStudents, docSelectedStudents)} className="rounded border-border" />
                            <span>{s.name}</span>
                            <span className="text-xs text-muted-foreground ml-auto">{s.email}</span>
                          </label>
                        ))}
                        {docSelectedStudents.length > 0 && (
                          <p className="text-xs text-primary px-1 pt-1">{docSelectedStudents.length} student(s) selected</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <Button onClick={handleFileUpload} className="w-full rounded-full bg-primary text-primary-foreground" disabled={docUploadFiles.length === 0 || uploading} data-testid="doc-upload-submit-btn">
                  {uploading ? uploadProgress || "Uploading..." : `Upload ${docUploadFiles.length > 1 ? `${docUploadFiles.length} Files` : "Document"}`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No documents yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <motion.div
                key={doc.document_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 p-4 rounded-xl border border-border/50 hover:border-primary/30 transition-all"
                data-testid={`document-${doc.document_id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{doc.original_filename}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {formatFileSize(doc.file_size)} · {doc.uploader_name} · {format(new Date(doc.created_at), "MMM d, yyyy")}
                    </p>
                    {isTeacher && doc.shared_with && doc.shared_with.length > 0 && (
                      <p className="text-xs text-blue-600 flex items-center gap-1 mt-0.5">
                        <Eye className="w-3 h-3" />
                        Shared with {doc.shared_with.length} student(s)
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => handleDownload(doc.document_id, doc.original_filename)} data-testid={`download-${doc.document_id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  {(doc.uploaded_by === user?.user_id || isTeacher) && (
                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleDeleteDocument(doc.document_id)} data-testid={`delete-${doc.document_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
